import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, Toggle, deletedRefProps, normalizeOptions } from './fields';
import EditHistoryLink from './EditHistoryLink';
import { LookupSearchField } from './LookupSearchField';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import { locationOptionLabel } from '../LocationTypeAhead';
import { EQUIPMENT_TYPES, ORDER_MOVE_TYPES, MELROSE_BILLTO_CUSTOMERCODES } from '../../constants/orderFieldOptions';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { MelroseOptions, getMelroseOptions } from '../../services/melrose.service';
import { TemplateMatch, searchTemplates } from '../../services/daltile.service';

// Template match card + validity check (shared behavior with the Dal Tile drawer —
// the same /daltile/template-search lookup).
const renderTemplateOption = (m: TemplateMatch): React.ReactNode => (
  <div className="relative w-full">
    {m.type && <span className="absolute right-0 top-0 text-[0.65625rem] font-bold text-slate-400 dark:text-slate-500">{m.type}</span>}
    <div className="pr-12 text-[0.8125rem] font-bold text-slate-800 dark:text-slate-100">
      {m.template}{m.customer ? <span className="font-normal text-slate-500 dark:text-slate-400"> · {titleCase(m.customer)}</span> : ''}
    </div>
    {m.destination && <div className="text-[0.75rem] text-slate-600 dark:text-slate-300">{titleCase(m.destination)}</div>}
    {(m.movetype || m.mode) && (
      <div className="mt-1 flex flex-wrap gap-x-3 text-[0.6875rem] text-slate-500 dark:text-slate-400">
        {m.movetype && <span>Move: <b className="text-slate-700 dark:text-slate-200">{m.movetype}</b></span>}
        {m.mode && <span>Mode: <b className="text-slate-700 dark:text-slate-200">{m.mode}</b></span>}
      </div>
    )}
  </div>
);
const templateStatus = async (v: string): Promise<string | null> => {
  const rs = await searchTemplates(v);
  return rs.some((r) => r.template.toUpperCase() === v.toUpperCase()) ? null : 'TEMPLATE DELETED';
};

/**
 * Bespoke edit/create drawer for Melrose Template Mapping (cms.melrose_templatemap).
 * Customer / Bill To enriched typeaheads, Consignee + Pickup location typeaheads,
 * Move Type / SCAC / Equip Type selects, a Max Lbs decimal, the shared Legal Template
 * search-as-you-type, and a Master Pro toggle. Two V1 rules: Bill To is editable only
 * for customercodes 5925 / 5860; and only ONE of Pickup / SCAC / Equip Type may be set
 * (choosing one locks the other two). Customer, Consignee, Move Type & Template required.
 *
 * Each FK picker carries deletedRefProps: the option lists hold ACTIVE records only, so a
 * mapping aimed at a since-deleted customer / bill-to / location keeps its name visible
 * ("Name · #recno") with a red note saying it was deleted — matching the grid's flag.
 */
const TABLE = 'melrose-templatemap';

const s = (v: any): string => (v == null ? '' : String(v));
const yn = (v: any): 'Y' | 'N' => (s(v).toUpperCase() === 'Y' ? 'Y' : 'N');
const fkSeed = (v: any): string => (v && String(v) !== '0' ? String(v) : '');
const fkOut = (v: any): string => (s(v).trim() || '0');

const MelroseTemplateMapEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [opts, setOpts] = useState<MelroseOptions | null>(null);
  const [form, setForm] = useState<TablemaintRow>({
    customercode: '', billto: '', consignee: '', movetype: '', pickup: '',
    scac: '', equiptype: '', maxlbs: '', template: '', singlemaster: 'N',
  });
  const [formDeleted, setFormDeleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getMelroseOptions().then(setOpts).catch(() => {});
    if (!isNew) tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
  }, [isNew, recno]);

  useEffect(() => {
    setForm({
      customercode: fkSeed(record.customercode),
      billto: fkSeed(record.billto),
      consignee: fkSeed(record.consignee),
      movetype: s(record.movetype),
      pickup: fkSeed(record.pickup),
      scac: s(record.scac),
      equiptype: s(record.equiptype),
      maxlbs: record.maxlbs != null && Number(record.maxlbs) !== 0 ? s(record.maxlbs) : '',
      template: s(record.template),
      singlemaster: yn(record.singlemaster),
    });
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record]);

  useEffect(() => { if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  // Enriched typeahead option lists.
  const customerOpts = useMemo(() => (opts?.customers ?? []).map((c) => ({
    value: String(c.recno), label: titleCase(s(c.custname)),
    labelSuffix: `#${c.recno}`,
    description: [titleCase(s(c.address)), titleCase(s(c.city)), s(c.state)].filter(Boolean).join(', ') + `  ·  ${s(c.custtype)}`,
  })), [opts]);
  const billtoOpts = useMemo(() => (opts?.billtos ?? []).map((b) => ({
    value: String(b.recno), label: titleCase(s(b.cname)),
    labelSuffix: `#${b.recno}`,
    description: [titleCase(s(b.address)), titleCase(s(b.city)), s(b.state)].filter(Boolean).join(', '),
  })), [opts]);
  const locationOpts = useMemo(() => (opts?.locations ?? []).map((l) => ({
    value: String(l.recno), label: locationOptionLabel(titleCase(s(l.lname)), s(l.cyalias)),
    labelSuffix: `#${l.recno}`,
    description: [titleCase(s(l.address)), titleCase(s(l.city)), s(l.state)].filter(Boolean).join(', '),
  })), [opts]);
  const scacOpts = useMemo(() => (opts?.scacs ?? []).map((x) => ({
    value: x.scac, label: x.name ? `${x.scac} - ${titleCase(x.name)}` : x.scac,
  })), [opts]);
  const movetypeOpts = useMemo(() => normalizeOptions(ORDER_MOVE_TYPES as unknown as string[]), []);
  const equiptypeOpts = useMemo(() => normalizeOptions(EQUIPMENT_TYPES as unknown as string[]), []);

  // V1 rule: Bill To editable only for these two customercodes.
  const billtoEditable = MELROSE_BILLTO_CUSTOMERCODES.includes(s(form.customercode));

  // V1 rule: only one of Pickup / SCAC / Equip Type. A field locks when it's blank AND
  // another of the three is set (the set one stays editable so it can be changed/cleared).
  const pickupSet = !!fkSeed(form.pickup);
  const scacSet = !!s(form.scac).trim();
  const equipSet = !!s(form.equiptype).trim();
  const pickupDisabled = isSaving || (!pickupSet && (scacSet || equipSet));
  const scacDisabled = isSaving || (!scacSet && (pickupSet || equipSet));
  const equipDisabled = isSaving || (!equipSet && (pickupSet || scacSet));

  const onCustomerChange = (v: string) => {
    setField('customercode', v);
    if (!MELROSE_BILLTO_CUSTOMERCODES.includes(v)) setField('billto', ''); // Bill To not applicable
  };

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!fkSeed(form.customercode)) e.push('Customer is required.');
    if (!fkSeed(form.consignee)) e.push('Consignee is required.');
    if (!s(form.movetype).trim()) e.push('Move Type is required.');
    if (!s(form.template).trim()) e.push('Template is required.');
    const nSet = [pickupSet, scacSet, equipSet].filter(Boolean).length;
    if (nSet > 1) e.push('Only one of Pickup Location, SCAC, or Equip Type may be set.');
    return e;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    const payload: TablemaintRow = {
      customercode: fkOut(form.customercode),
      billto: fkOut(form.billto),
      consignee: fkOut(form.consignee),
      movetype: s(form.movetype),
      pickup: fkOut(form.pickup),
      scac: s(form.scac),
      equiptype: s(form.equiptype),
      maxlbs: s(form.maxlbs).trim() || '0',
      template: s(form.template),
      singlemaster: yn(form.singlemaster),
    };
    try {
      if (isNew) {
        await tablemaintService.create(TABLE, payload);
      } else {
        const patch: TablemaintRow = {};
        (['customercode', 'billto', 'consignee', 'pickup'] as const).forEach((k) => {
          if (fkOut(form[k]) !== fkOut(record[k])) patch[k] = fkOut(form[k]);
        });
        (['movetype', 'scac', 'equiptype', 'template'] as const).forEach((k) => {
          if (s(form[k]) !== s(record[k])) patch[k] = s(form[k]);
        });
        if ((s(form.maxlbs).trim() || '0') !== (s(record.maxlbs).trim() || '0')) patch.maxlbs = payload.maxlbs;
        if (yn(form.singlemaster) !== yn(record.singlemaster)) patch.singlemaster = payload.singlemaster;
        if (formDeleted !== (s(record.deleted) === 'Y')) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save template mapping.');
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, isNew, form, record, formDeleted, recno, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Template Map' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add Melrose Template Map' : `Edit Melrose Template Map (#${recno})`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="56rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Parties */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Parties</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="Customer" required value={s(form.customercode)} onChange={onCustomerChange}
              options={customerOpts} disabled={isSaving} showDescriptions showSelectedInfo
              {...deletedRefProps('Customer', record.custname, form.customercode, record.custdeleted)} />
            <div>
              <SelectField label="Bill To" value={s(form.billto)} onChange={(v) => setField('billto', v)}
                options={billtoOpts} disabled={isSaving || !billtoEditable} showDescriptions showSelectedInfo
                {...deletedRefProps('Bill To', record.billtoname, form.billto, record.billtodeleted)} />
              {!billtoEditable && (
                <p className="mt-1 text-xs text-muted">
                  Bill To is editable only for specific Melrose customers.
                </p>
              )}
            </div>
            <SelectField label="Consignee" required value={s(form.consignee)} onChange={(v) => setField('consignee', v)}
              options={locationOpts} disabled={isSaving} showDescriptions showSelectedInfo
              {...deletedRefProps('Consignee', record.consigneename, form.consignee, record.consigneedeleted)} />
            <SelectField label="Move Type" required value={s(form.movetype)} onChange={(v) => setField('movetype', v)}
              options={movetypeOpts} disabled={isSaving} />
          </div>
        </fieldset>

        {/* Assignment — exactly one of Pickup / SCAC / Equip Type */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">
            Assignment <span className="text-xs font-normal text-gray-400">(choose one: Pickup, SCAC, or Equip Type)</span>
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SelectField label="Pickup Location" value={s(form.pickup)} onChange={(v) => setField('pickup', v)}
              options={locationOpts} disabled={pickupDisabled} showDescriptions showSelectedInfo
              {...deletedRefProps('Pickup Location', record.pickupname, form.pickup, record.pickupdeleted)} />
            <SelectField label="SCAC" value={s(form.scac)} onChange={(v) => setField('scac', v)}
              options={scacOpts} disabled={scacDisabled}
              {...deletedRefProps('SCAC', form.scac, form.scac, record.scacdeleted)} />
            <SelectField label="Equip Type" value={s(form.equiptype)} onChange={(v) => setField('equiptype', v)}
              options={equiptypeOpts} disabled={equipDisabled} />
          </div>
        </fieldset>

        {/* Template + details */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
          <legend className="px-2 text-sm font-semibold text-label">Template</legend>
          <LookupSearchField<TemplateMatch>
            label="Template" required
            displayValue={s(form.template)}
            onType={(t) => setField('template', t)}
            onSelect={(m) => setField('template', m.template)}
            search={searchTemplates} renderItem={renderTemplateOption}
            itemValue={(m) => m.template} itemLabel={(m) => m.template}
            statusValue={s(form.template)} checkStatus={templateStatus}
            placeholder="Type a template # or destination…" disabled={isSaving}
            clearOnBlurWithoutSelect
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-label mb-1">Max Lbs</label>
              <input type="number" step="any" min="0" value={s(form.maxlbs)} disabled={isSaving}
                onChange={(e) => setField('maxlbs', e.target.value)} placeholder="(no limit)"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
            </div>
            <div className="flex items-end pb-1">
              <Toggle label="Master Pro" checked={s(form.singlemaster) === 'Y'} disabled={isSaving}
                onChange={(v) => setField('singlemaster', v ? 'Y' : 'N')} />
            </div>
          </div>
        </fieldset>

        {!isNew && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <Toggle label="Deleted" checked={formDeleted} disabled={isSaving} onChange={setFormDeleted}
              hint="Soft-deletes this mapping — hidden from filters but preserved in history. Uncheck and save to restore." />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default MelroseTemplateMapEditDrawer;
